import type { SemanticBeat, FormatId } from "./director";

/**
 * Anivaze v4 — thin client for the AI Director endpoint.
 *
 * Calls the orchestrator worker's `/generate/storyboard`, which returns SEMANTIC
 * beats (role/title/narration) from the worker AI model. The caller compiles them
 * into a full storyboard deterministically. Callers should catch and fall back to
 * the local deterministic Director so the UI works offline. See PLAN §11, §21.
 */

const API_BASE: string =
  (import.meta.env.VITE_ORCHESTRATOR_BASE_URL as string | undefined) ||
  "https://learnengine-orchestrator-api.obedpaul-p.workers.dev";

/** Stable per-browser id — identifies a "person" for the one-free-video rule
 *  (ANIMATION-STACK §4) and keys rate limits. Falls back cleanly when storage
 *  is unavailable (private mode); the server also fingerprints by hashed IP. */
const USER_ID: string = (() => {
  const env = import.meta.env.VITE_USER_ID as string | undefined;
  if (env) return env;
  try {
    const k = "anivaze-uid";
    let v = localStorage.getItem(k);
    if (!v) {
      v = crypto.randomUUID();
      localStorage.setItem(k, v);
    }
    return v;
  } catch {
    return "anonymous";
  }
})();

/** Shared with the other API clients (tts, assets) so all calls identify alike. */
export const CLIENT_ID = USER_ID;

export interface StoryboardResponse {
  beats: SemanticBeat[];
  source: "ai" | "fallback";
}

export async function requestStoryboard(
  input: { topic: string; format: FormatId; targetSeconds?: number; language?: string },
  signal?: AbortSignal,
): Promise<StoryboardResponse> {
  const res = await fetch(`${API_BASE}/generate/storyboard`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-user-id": USER_ID },
    body: JSON.stringify(input),
    signal,
  });
  if (res.status === 422) {
    const d: any = await res.json().catch(() => ({}));
    if (d?.blocked) { const e = new Error(d.reason || "Anivaze makes learning videos only — it can't help with code, homework answers, or unsafe topics. Try a topic you'd like explained, e.g. \"how do black holes form\"."); (e as any).blocked = true; throw e; }
  }
  if (res.status === 402) {
    const d: any = await res.json().catch(() => ({}));
    // Paywall reuses the no-fallback path: show the message, don't generate.
    if (d?.paywall) { const e = new Error(d.reason || "You've used your free video — plans start at $5.99/month."); (e as any).blocked = true; throw e; }
  }
  if (!res.ok) throw new Error(`storyboard request failed: ${res.status}`);
  const data: any = await res.json();
  if (!data?.ok || !Array.isArray(data.beats)) throw new Error("storyboard request returned no beats");
  return { beats: data.beats as SemanticBeat[], source: data.source === "ai" ? "ai" : "fallback" };
}

// ── YouTube publish (PRODUCT-CHAT-EDIT §8) ───────────────────────────────────
export interface YouTubeStatus { configured: boolean; linked: boolean; channel: string }

export async function youtubeStatus(): Promise<YouTubeStatus> {
  try {
    const res = await fetch(`${API_BASE}/youtube/status`, { headers: { "x-user-id": USER_ID } });
    const d: any = await res.json();
    return { configured: !!d?.configured, linked: !!d?.linked, channel: String(d?.channel ?? "") };
  } catch { return { configured: false, linked: false, channel: "" }; }
}

/** The Google consent URL to link an account (or null if not configured). */
export async function youtubeAuthUrl(returnUrl: string): Promise<string | null> {
  const res = await fetch(`${API_BASE}/youtube/auth?app=${encodeURIComponent(returnUrl)}`, { headers: { "x-user-id": USER_ID } });
  if (!res.ok) return null;
  const d: any = await res.json();
  return d?.url ?? null;
}

/** A short-lived access token minted from the stored refresh token (for the upload). */
export async function youtubeToken(): Promise<string> {
  const res = await fetch(`${API_BASE}/youtube/token`, { method: "POST", headers: { "x-user-id": USER_ID } });
  if (!res.ok) throw new Error(`token ${res.status}`);
  const d: any = await res.json();
  if (!d?.accessToken) throw new Error("no access token");
  return d.accessToken;
}

/** Resumable upload of the exported video straight from the browser to YouTube. */
export async function uploadToYouTube(
  accessToken: string,
  blob: Blob,
  meta: { title: string; description: string; privacy?: "unlisted" | "private" | "public" },
): Promise<{ videoId: string; url: string }> {
  const init = await fetch("https://www.googleapis.com/upload/youtube/v3/videos?uploadType=resumable&part=snippet,status", {
    method: "POST",
    headers: {
      authorization: `Bearer ${accessToken}`,
      "content-type": "application/json; charset=UTF-8",
      "x-upload-content-type": blob.type || "video/mp4",
    },
    body: JSON.stringify({
      snippet: { title: meta.title.slice(0, 100), description: meta.description.slice(0, 4900), categoryId: "27" },
      status: { privacyStatus: meta.privacy ?? "unlisted", selfDeclaredMadeForKids: false },
    }),
  });
  if (!init.ok) throw new Error(`upload init failed: ${init.status}`);
  const uploadUrl = init.headers.get("location");
  if (!uploadUrl) throw new Error("no resumable upload URL");
  const put = await fetch(uploadUrl, { method: "PUT", headers: { "content-type": blob.type || "video/mp4" }, body: blob });
  if (!put.ok) throw new Error(`upload failed: ${put.status}`);
  const d: any = await put.json();
  return { videoId: d.id, url: `https://youtu.be/${d.id}` };
}

export interface SourcedImage { imageUrl: string; attribution: string; license: string; sourceUrl: string }

/** Pull a real, LICENSED photo (Wikimedia) for a subject the generator can't invent. */
export async function sourceRealImage(query: string): Promise<SourcedImage> {
  const res = await fetch(`${API_BASE}/assets/source`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-user-id": USER_ID },
    body: JSON.stringify({ query }),
  });
  if (!res.ok) throw new Error(`source failed: ${res.status}`);
  const d: any = await res.json();
  if (!d?.url) throw new Error("no image found");
  return {
    imageUrl: d.url.startsWith("http") ? d.url : `${API_BASE}${d.url}`,
    attribution: String(d.attribution ?? "Wikimedia Commons"),
    license: String(d.license ?? ""),
    sourceUrl: String(d.sourceUrl ?? ""),
  };
}

export interface SavedProject { projectId: string; kind: string; title: string; daysLeft: number; createdAt: number }

/** Save the current storyboard spec (kept 30d → transcript 90d → purged). */
export async function saveProject(spec: unknown, title?: string): Promise<{ projectId: string; daysLeft: number }> {
  const res = await fetch(`${API_BASE}/projects`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-user-id": USER_ID },
    body: JSON.stringify({ spec, title }),
  });
  if (!res.ok) throw new Error(`save failed: ${res.status}`);
  const d: any = await res.json();
  return { projectId: d.projectId, daysLeft: d.daysLeft ?? 30 };
}

/** List the user's saved projects with days-left. */
export async function listProjects(): Promise<SavedProject[]> {
  const res = await fetch(`${API_BASE}/projects`, { headers: { "x-user-id": USER_ID } });
  if (!res.ok) return [];
  const d: any = await res.json();
  return Array.isArray(d?.projects) ? d.projects : [];
}

/** Load one saved project's spec back. */
export async function getProject(id: string): Promise<any | null> {
  const res = await fetch(`${API_BASE}/projects/${id}`, { headers: { "x-user-id": USER_ID } });
  if (!res.ok) return null;
  const d: any = await res.json();
  return d?.data ?? null;
}

export async function deleteProject(id: string): Promise<void> {
  await fetch(`${API_BASE}/projects/${id}`, { method: "DELETE", headers: { "x-user-id": USER_ID } });
}

export interface ChatTurn { role: "user" | "assistant"; content: string }
export interface ChatReply { reply: string; action: "allow" | "refuse" | "redirect" }

/** Learning chat (guardrailed, free model). Returns the reply + guardrail action. */
export async function sendChat(messages: ChatTurn[], signal?: AbortSignal): Promise<ChatReply> {
  const res = await fetch(`${API_BASE}/chat`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-user-id": USER_ID },
    body: JSON.stringify({ messages }),
    signal,
  });
  if (!res.ok) throw new Error(`chat request failed: ${res.status}`);
  const data: any = await res.json();
  return { reply: String(data?.reply ?? ""), action: data?.action === "refuse" ? "refuse" : data?.action === "redirect" ? "redirect" : "allow" };
}
